import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Wallet, Package, ShoppingCart, Users2, Upload, FileText,
  Plus, Trash2, Edit2, AlertTriangle, BarChart3, Save, X, PiggyBank, Calendar, RefreshCcw,
} from "lucide-react";
import BilancioPanel from "./BilancioPanel";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";

const CURRENCY = (n) => `€${(Number(n) || 0).toFixed(2)}`;
const TODAY = () => new Date().toISOString().slice(0, 10);
const MONTH = () => new Date().toISOString().slice(0, 7);

const SUB_TABS = [
  { id: "dashboard", label: "Dashboard", icon: BarChart3 },
  { id: "bilancio", label: "Bilancio", icon: PiggyBank },
  { id: "income", label: "Entrate", icon: TrendingUp },
  { id: "expense", label: "Uscite", icon: TrendingDown },
  { id: "inventory", label: "Magazzino", icon: Package },
  { id: "shopping", label: "Lista acquisti", icon: ShoppingCart },
  { id: "suppliers", label: "Fornitori", icon: Users2 },
  { id: "imports", label: "Import Booking/Airbnb", icon: Upload },
];

export default function ManagementTab() {
  const [active, setActive] = useState("dashboard");

  return (
    <section data-testid="management-tab" className="space-y-4">
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-1 inline-flex flex-wrap gap-1">
        {SUB_TABS.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setActive(s.id)}
              data-testid={`mgmt-sub-${s.id}`}
              className={`px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition ${
                active === s.id ? "bg-[#1E3A5F] text-white" : "text-[#635F59] hover:bg-[#FDFBF7]"
              }`}
            >
              <Icon className="w-4 h-4" />
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-[#E8E2D9] rounded-xl p-5">
        {active === "dashboard" && <DashboardPanel />}
        {active === "bilancio" && <BilancioPanel />}
        {active === "income" && <TxnPanel kind="income" />}
        {active === "expense" && <TxnPanel kind="expense" />}
        {active === "inventory" && <InventoryPanel />}
        {active === "shopping" && <ShoppingPanel />}
        {active === "suppliers" && <SuppliersPanel />}
        {active === "imports" && <ImportPanel />}
      </div>
    </section>
  );
}

/* ---------------- Dashboard ---------------- */
function DashboardPanel() {
  const [month, setMonth] = useState(MONTH());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/finance/balance?month=${month}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [month]);

  if (loading || !data) return <div className="text-[#635F59]">Caricamento...</div>;

  const cards = [
    { label: "Entrate", value: data.income, icon: TrendingUp, color: "emerald" },
    { label: "Uscite", value: data.expense, icon: TrendingDown, color: "rose" },
    { label: "Tasse", value: data.tax, icon: Wallet, color: "amber" },
    { label: "Bilancio", value: data.net, icon: BarChart3, color: data.net >= 0 ? "blue" : "rose" },
  ];
  const colorMap = {
    emerald: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-700",
    rose: "from-rose-50 to-rose-100 border-rose-200 text-rose-700",
    amber: "from-amber-50 to-amber-100 border-amber-200 text-amber-800",
    blue: "from-blue-50 to-blue-100 border-blue-200 text-blue-700",
  };

  return (
    <div className="space-y-5" data-testid="mgmt-dashboard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-display text-xl text-[#1E3A5F]">Bilancio mensile</h3>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="max-w-[200px]" data-testid="dashboard-month" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className={`bg-gradient-to-br ${colorMap[c.color]} border rounded-xl p-4`} data-testid={`dashboard-card-${c.label.toLowerCase()}`}>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80 mb-1">
                <Icon className="w-3.5 h-3.5" /> {c.label}
              </div>
              <div className="font-display text-2xl font-bold">{CURRENCY(c.value)}</div>
            </div>
          );
        })}
      </div>

      {data.by_category?.length > 0 && (
        <div>
          <h4 className="font-semibold text-[#1E3A5F] mb-2">Per categoria</h4>
          <div className="space-y-1.5">
            {data.by_category.map((c) => (
              <div key={c.category} className="flex items-center justify-between border border-[#E8E2D9] rounded-lg p-2.5 text-sm">
                <span className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${c.type === "income" ? "bg-emerald-500" : c.type === "expense" ? "bg-rose-500" : "bg-amber-500"}`} />
                  <span className="font-mono text-[#1E3A5F]">{c.category}</span>
                  <span className="text-xs text-[#635F59]">({c.count})</span>
                </span>
                <span className={`font-semibold ${c.type === "income" ? "text-emerald-700" : "text-rose-700"}`}>{CURRENCY(c.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Income / Expense ---------------- */
function TxnPanel({ kind }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: "", amount: "", vat_amount: "", description: "", date: TODAY() });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/finance/transactions?type=${kind}`);
      setList(r.data.transactions || []);
    } catch { toast.error("Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [kind]);

  const submit = async () => {
    if (!form.category || !form.amount) return toast.error("Categoria e importo obbligatori");
    try {
      await api.post("/admin/finance/transactions", {
        type: kind,
        category: form.category,
        amount: parseFloat(form.amount) || 0,
        vat_amount: parseFloat(form.vat_amount) || 0,
        description: form.description,
        date: form.date,
        source: "manual",
      });
      toast.success("Aggiunto");
      setShowForm(false);
      setForm({ category: "", amount: "", vat_amount: "", description: "", date: TODAY() });
      load();
    } catch (err) { toast.error(errMsg(err, "Errore")); }
  };

  const remove = async (id) => {
    if (!confirm("Eliminare la transazione?")) return;
    await api.delete(`/admin/finance/transactions/${id}`);
    load();
  };

  const total = useMemo(() => list.reduce((s, t) => s + (t.amount || 0), 0), [list]);
  const isIncome = kind === "income";

  return (
    <div className="space-y-4" data-testid={`mgmt-${kind}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
            {isIncome ? <TrendingUp className="w-5 h-5 text-emerald-600" /> : <TrendingDown className="w-5 h-5 text-rose-600" />}
            {isIncome ? "Entrate" : "Uscite"} ({list.length})
          </h3>
          <p className="text-xs text-[#635F59]">Totale: <strong className={isIncome ? "text-emerald-700" : "text-rose-700"}>{CURRENCY(total)}</strong></p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#1E3A5F] hover:bg-[#152A45] text-white" data-testid={`btn-add-${kind}`}>
          <Plus className="w-4 h-4 mr-2" /> Nuova {isIncome ? "entrata" : "uscita"}
        </Button>
      </div>

      {showForm && (
        <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-4 space-y-3" data-testid={`form-${kind}`}>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Categoria *</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder={isIncome ? "es. booking_direct, contanti" : "es. utenze, manutenzione"} className="mt-1" />
            </div>
            <div>
              <Label>Data</Label>
              <DateInputIT value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Importo €</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>IVA €</Label>
              <Input type="number" step="0.01" value={form.vat_amount} onChange={(e) => setForm({ ...form, vat_amount: e.target.value })} className="mt-1" />
            </div>
          </div>
          <div>
            <Label>Descrizione</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" />
          </div>
          <div className="flex gap-2">
            <Button onClick={submit} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid={`save-${kind}`}><Save className="w-4 h-4 mr-2" /> Salva</Button>
            <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        {loading && <div className="text-[#635F59]">Caricamento...</div>}
        {!loading && list.length === 0 && <div className="text-sm text-[#635F59] text-center py-6">Nessuna transazione</div>}
        {list.map((t) => (
          <div key={t.id} className="flex items-center gap-3 border border-[#E8E2D9] rounded-lg p-2.5 text-sm" data-testid={`txn-${t.id}`}>
            <span className="text-xs text-[#635F59] font-mono w-24 shrink-0">{t.date}</span>
            <span className="flex-1 min-w-0">
              <span className="font-mono text-[#1E3A5F] text-xs">{t.category}</span>
              {t.description && <span className="text-[#635F59] ml-2">— {t.description}</span>}
            </span>
            <span className={`font-semibold shrink-0 ${isIncome ? "text-emerald-700" : "text-rose-700"}`}>{CURRENCY(t.amount)}</span>
            {t.source !== "manual" && <span className="text-[10px] text-[#9C9890] uppercase tracking-wider shrink-0">{t.source}</span>}
            <button onClick={() => remove(t.id)} className="text-rose-500 hover:bg-rose-50 rounded p-1" aria-label="Elimina"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Inventory ---------------- */
function InventoryPanel() {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", category: "generale", unit: "pz", current_qty: 0, min_qty: 0, last_purchase_price: "", supplier_id: "", notes: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const r = await api.get("/admin/inventory");
    setList(r.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const startEdit = (item) => {
    setEditing(item.id);
    setForm({ ...empty, ...item, last_purchase_price: item.last_purchase_price || "", supplier_id: item.supplier_id || "" });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.name) return toast.error("Nome obbligatorio");
    const payload = {
      ...form,
      current_qty: parseFloat(form.current_qty) || 0,
      min_qty: parseFloat(form.min_qty) || 0,
      last_purchase_price: form.last_purchase_price ? parseFloat(form.last_purchase_price) : null,
      supplier_id: form.supplier_id || null,
    };
    try {
      if (editing) await api.put(`/admin/inventory/${editing}`, payload);
      else await api.post("/admin/inventory", payload);
      toast.success("Salvato");
      setShowForm(false); setEditing(null); setForm(empty);
      load();
    } catch (err) { toast.error(errMsg(err, "Errore")); }
  };

  const remove = async (id) => {
    if (!confirm("Eliminare l'articolo?")) return;
    await api.delete(`/admin/inventory/${id}`);
    load();
  };

  return (
    <div className="space-y-4" data-testid="mgmt-inventory">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl text-[#1E3A5F]">Magazzino ({list.length})</h3>
          <p className="text-xs text-[#635F59]">Quando la scorta scende sotto il minimo, l'articolo viene aggiunto automaticamente alla lista acquisti.</p>
        </div>
        <Button onClick={() => { setShowForm(!showForm); setEditing(null); setForm(empty); }} className="bg-[#1E3A5F] hover:bg-[#152A45] text-white" data-testid="btn-add-inventory">
          <Plus className="w-4 h-4 mr-2" /> Nuovo articolo
        </Button>
      </div>

      {showForm && (
        <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-4 space-y-3">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2"><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Unità</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1" placeholder="pz, kg, conf" /></div>
            <div><Label>Quantità attuale</Label><Input type="number" step="0.01" value={form.current_qty} onChange={(e) => setForm({ ...form, current_qty: e.target.value })} className="mt-1" /></div>
            <div><Label>Soglia minima</Label><Input type="number" step="0.01" value={form.min_qty} onChange={(e) => setForm({ ...form, min_qty: e.target.value })} className="mt-1" /></div>
            <div><Label>Prezzo ultimo acquisto €</Label><Input type="number" step="0.01" value={form.last_purchase_price} onChange={(e) => setForm({ ...form, last_purchase_price: e.target.value })} className="mt-1" /></div>
            <div className="sm:col-span-3"><Label>Categoria</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1" placeholder="colazione, pulizia, biancheria..." /></div>
            <div className="sm:col-span-3"><Label>Note</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={submit} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"><Save className="w-4 h-4 mr-2" /> Salva</Button>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Annulla</Button>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        {list.length === 0 && <div className="text-sm text-[#635F59] text-center py-6">Magazzino vuoto. Aggiungi il primo articolo.</div>}
        {list.map((it) => {
          const low = (it.current_qty ?? 0) < (it.min_qty ?? 0);
          return (
            <div key={it.id} className={`flex items-center gap-3 border rounded-lg p-2.5 text-sm ${low ? "border-rose-200 bg-rose-50" : "border-[#E8E2D9]"}`} data-testid={`inv-${it.id}`}>
              {low && <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />}
              <span className="flex-1 min-w-0">
                <span className="font-semibold text-[#1E3A5F]">{it.name}</span>
                <span className="text-xs text-[#635F59] ml-2">{it.category}</span>
              </span>
              <span className={`font-mono text-sm shrink-0 ${low ? "text-rose-700" : "text-[#1E3A5F]"}`}>
                <strong>{it.current_qty}</strong>/{it.min_qty} {it.unit}
              </span>
              {it.last_purchase_price ? <span className="text-xs text-[#635F59] shrink-0">{CURRENCY(it.last_purchase_price)}</span> : null}
              <button onClick={() => startEdit(it)} className="text-[#1E3A5F] hover:bg-[#FDFBF7] rounded p-1" aria-label="Modifica"><Edit2 className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(it.id)} className="text-rose-500 hover:bg-rose-50 rounded p-1" aria-label="Elimina"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Shopping ---------------- */
function ShoppingPanel() {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const empty = { name: "", qty: 1, unit: "pz", priority: "normal", notes: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const r = await api.get("/admin/shopping-list");
    setList(r.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name) return toast.error("Nome obbligatorio");
    await api.post("/admin/shopping-list", { ...form, qty: parseFloat(form.qty) || 1 });
    toast.success("Aggiunto");
    setShowForm(false); setForm(empty); load();
  };
  const markPurchased = async (id) => { await api.patch(`/admin/shopping-list/${id}`, { status: "purchased" }); load(); };
  const remove = async (id) => { await api.delete(`/admin/shopping-list/${id}`); load(); };

  const colorByPrio = { urgent: "bg-rose-100 text-rose-700 border-rose-200", high: "bg-amber-100 text-amber-700 border-amber-200", normal: "bg-blue-50 text-blue-700 border-blue-200", low: "bg-[#FDFBF7] text-[#635F59] border-[#E8E2D9]" };

  return (
    <div className="space-y-4" data-testid="mgmt-shopping">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl text-[#1E3A5F]">Lista acquisti ({list.length})</h3>
          <p className="text-xs text-[#635F59]">Articoli da comprare. Quelli con badge <em>auto</em> arrivano dal magazzino sotto soglia.</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#1E3A5F] hover:bg-[#152A45] text-white" data-testid="btn-add-shopping">
          <Plus className="w-4 h-4 mr-2" /> Aggiungi
        </Button>
      </div>

      {showForm && (
        <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-4 space-y-3">
          <div className="grid sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2"><Label>Articolo *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Quantità</Label><Input type="number" step="0.01" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="mt-1" /></div>
            <div><Label>Priorità</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Bassa</SelectItem>
                  <SelectItem value="normal">Normale</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Note" />
          <div className="flex gap-2">
            <Button onClick={submit} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"><Save className="w-4 h-4 mr-2" /> Salva</Button>
            <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        {list.length === 0 && <div className="text-sm text-[#635F59] text-center py-6">Nessun articolo da comprare 👌</div>}
        {list.map((it) => (
          <div key={it.id} className="flex items-center gap-3 border border-[#E8E2D9] rounded-lg p-2.5 text-sm bg-white" data-testid={`sh-${it.id}`}>
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${colorByPrio[it.priority] || colorByPrio.normal}`}>{it.priority}</span>
            <span className="flex-1 min-w-0">
              <span className="font-semibold text-[#1E3A5F]">{it.name}</span>
              <span className="text-xs text-[#635F59] ml-2">{it.qty} {it.unit}</span>
              {it.auto_added && <span className="text-[10px] text-amber-700 ml-2 uppercase">auto</span>}
              {it.notes && <div className="text-xs text-[#635F59] mt-0.5 italic">{it.notes}</div>}
            </span>
            <Button size="sm" variant="outline" onClick={() => markPurchased(it.id)} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"><X className="w-3.5 h-3.5 mr-1" /> Comprato</Button>
            <button onClick={() => remove(it.id)} className="text-rose-500 hover:bg-rose-50 rounded p-1" aria-label="Elimina"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Suppliers ---------------- */
function SuppliersPanel() {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", type: "generico", phone: "", email: "", address: "", vat_number: "", products: "", notes: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const r = await api.get("/admin/suppliers");
    setList(r.data.suppliers || []);
  };
  useEffect(() => { load(); }, []);

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({ ...empty, ...s, products: (s.products || []).join(", ") });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.name) return toast.error("Nome obbligatorio");
    const payload = { ...form, products: form.products.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/admin/suppliers/${editing}`, payload);
      else await api.post("/admin/suppliers", payload);
      toast.success("Salvato");
      setShowForm(false); setEditing(null); setForm(empty); load();
    } catch (err) { toast.error(errMsg(err, "Errore")); }
  };

  const remove = async (id) => { if (!confirm("Eliminare il fornitore?")) return; await api.delete(`/admin/suppliers/${id}`); load(); };

  return (
    <div className="space-y-4" data-testid="mgmt-suppliers">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-display text-xl text-[#1E3A5F]">Rubrica fornitori ({list.length})</h3>
        <Button onClick={() => { setShowForm(!showForm); setEditing(null); setForm(empty); }} className="bg-[#1E3A5F] hover:bg-[#152A45] text-white" data-testid="btn-add-supplier">
          <Plus className="w-4 h-4 mr-2" /> Nuovo fornitore
        </Button>
      </div>

      {showForm && (
        <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Tipo</Label><Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1" placeholder="alimentari, lavanderia, manutenzione..." /></div>
            <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><Label>P.IVA</Label><Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} className="mt-1" /></div>
            <div><Label>Indirizzo</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>Prodotti (separati da virgola)</Label><Input value={form.products} onChange={(e) => setForm({ ...form, products: e.target.value })} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" /></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={submit} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"><Save className="w-4 h-4 mr-2" /> Salva</Button>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Annulla</Button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {list.length === 0 && <div className="text-sm text-[#635F59] col-span-full text-center py-6">Nessun fornitore. Aggiungi il primo per iniziare.</div>}
        {list.map((s) => (
          <div key={s.id} className="border border-[#E8E2D9] rounded-lg p-3" data-testid={`sup-${s.id}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="font-semibold text-[#1E3A5F]">{s.name}</div>
              <div className="flex gap-1">
                <button onClick={() => startEdit(s)} className="text-[#1E3A5F] hover:bg-[#FDFBF7] rounded p-1"><Edit2 className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(s.id)} className="text-rose-500 hover:bg-rose-50 rounded p-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="text-xs text-[#635F59] uppercase tracking-wider mb-2">{s.type}</div>
            <div className="text-xs text-[#2D2926] space-y-0.5">
              {s.phone && <div>📞 {s.phone}</div>}
              {s.email && <div>✉️ {s.email}</div>}
              {s.address && <div>📍 {s.address}</div>}
              {s.vat_number && <div>P.IVA {s.vat_number}</div>}
              {s.products?.length > 0 && <div className="text-[#635F59] italic mt-1">{s.products.join(" · ")}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Imports (Booking/Airbnb statements) ---------------- */
function ImportPanel() {
  const [list, setList] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // {source, period, rows, totals, payout, document_url}
  const [parsing, setParsing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Annual cedolare report
  const [report, setReport] = useState(null);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());

  const load = async () => {
    const r = await api.get("/admin/finance/imports");
    setList(r.data.imports || []);
  };
  const loadReport = async (y = reportYear) => {
    try {
      const r = await api.get(`/admin/finance/cedolare-annual-report?year=${y}`);
      setReport(r.data);
    } catch (e) { console.warn("cedolare report failed", e); }
  };
  useEffect(() => { load(); loadReport(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const SRC_LABEL = {
    booking: "Booking.com",
    airbnb: "Airbnb",
    booking_cedolare: "Booking.com — Cedolare secca",
    booking_invoice: "Booking.com — Fattura commissioni",
    airbnb_invoices: "Airbnb — Fatture commissioni",
  };

  const onPreview = async () => {
    if (!file) { toast.error("Seleziona un file CSV o XLSX"); return; }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/finance/import-statement/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      toast.success(`Riconosciuto: ${SRC_LABEL[r.data.source] || r.data.source} — ${r.data.totals?.count || 0} prenotazioni`);
    } catch (err) { toast.error(errMsg(err, "Errore durante il parsing")); }
    finally { setParsing(false); }
  };

  const onConfirm = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const payload = {
        source: preview.source,
        period: preview.period,
        document_url: preview.document_url,
        rows: preview.rows,
      };
      const r = await api.post("/admin/finance/import-statement/confirm", payload);
      toast.success(`Importato: ${r.data.reservations} prenotazioni → ${r.data.transactions_created} transazioni`);
      setPreview(null);
      setFile(null);
      load();
      loadReport();
    } catch (err) { toast.error(errMsg(err, "Errore conferma import")); }
    finally { setConfirming(false); }
  };

  const onRollback = async (id) => {
    if (!confirm("Sicuro di voler eliminare questo import e tutte le sue transazioni?")) return;
    try {
      const r = await api.delete(`/admin/finance/imports/${id}`);
      toast.success(`Rimosse ${r.data.deleted_transactions} transazioni`);
      load();
    } catch (err) { toast.error(errMsg(err, "Errore")); }
  };

  return (
    <div className="space-y-5" data-testid="mgmt-imports">
      <div>
        <h3 className="font-display text-xl text-[#1E3A5F]">Import Booking.com / Airbnb</h3>
        <p className="text-xs text-[#635F59]">Carica lo Statement (CSV o XLSX). Il parser riconosce automaticamente formato Booking.com, Airbnb o ritenuta cedolare secca, mostra l'anteprima e poi crea una transazione per ogni prenotazione.</p>
      </div>

      {/* Upload */}
      {!preview && (
        <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-4 space-y-3">
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label>File statement (CSV / XLSX / XLS / PDF)</Label>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1"
                data-testid="import-file"
              />
              <p className="text-[11px] text-[#635F59] mt-1">
                Formati supportati: Booking <code>Arrivo_*.xls</code>, <code>reservation_statements_overview.csv</code>, Airbnb <code>reservations.csv</code> / <code>airbnb_*.csv</code>, fatture commissioni <code>*.csv</code>, Booking PDF cedolare/fattura, Booking <code>Riepilogo cedolare.xlsx</code>
              </p>
            </div>
            <Button onClick={onPreview} disabled={parsing || !file} className="bg-[#1E3A5F] hover:bg-[#15294A] text-white" data-testid="btn-parse-statement">
              <FileText className="w-4 h-4 mr-2" /> {parsing ? "Analisi..." : "Analizza file"}
            </Button>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="space-y-4" data-testid="import-preview">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1E3A5F] to-[#15294A] text-white rounded-lg p-4 flex flex-wrap items-center gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Sorgente rilevata</div>
              <div className="font-display text-lg">{SRC_LABEL[preview.source] || preview.source}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Periodo</div>
              <div className="font-mono text-sm">{preview.period || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Prenotazioni</div>
              <div className="font-mono text-sm">{preview.totals?.count || 0}{preview.totals?.count_cancelled ? ` (+${preview.totals.count_cancelled} cancellate)` : ""}</div>
            </div>
            {preview.payout && (
              <div className="ml-auto text-right">
                <div className="text-[10px] uppercase tracking-widest opacity-70">Payout {preview.payout.date}</div>
                <div className="font-display text-lg">{CURRENCY(preview.payout.amount)}</div>
              </div>
            )}
          </div>

          {preview.source === "booking_cedolare" && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <b>Documento ritenuta cedolare secca:</b> verranno create solo le voci di ritenuta fiscale (21%). Le entrate lorde e le commissioni vanno importate separatamente con lo Statement principale di Booking.com per evitare doppi conteggi.
              </div>
            </div>
          )}

          {/* Totals grid */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[
              { label: "Lordo", val: preview.totals?.gross, color: "text-emerald-700" },
              { label: "Commissione", val: preview.totals?.commission, color: "text-rose-700" },
              { label: "Payment fee", val: preview.totals?.payment_fee, color: "text-rose-700" },
              { label: "Tassa soggiorno", val: preview.totals?.city_tax, color: "text-amber-700" },
              { label: preview.source === "booking_cedolare" ? "Ritenuta 21%" : "Netto", val: preview.source === "booking_cedolare" ? preview.totals?.withholding : preview.totals?.net, color: "text-[#1E3A5F]" },
            ].map((c) => (
              <div key={c.label} className="bg-white border border-[#E8E2D9] rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-[#635F59]">{c.label}</div>
                <div className={`font-mono font-bold text-base ${c.color}`}>{CURRENCY(c.val)}</div>
              </div>
            ))}
          </div>

          {/* Rows table */}
          <div className="border border-[#E8E2D9] rounded-lg overflow-hidden">
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#FDFBF7] text-[#635F59] sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-semibold">Codice</th>
                    <th className="text-left p-2 font-semibold">Ospite</th>
                    <th className="text-left p-2 font-semibold">Check-in</th>
                    <th className="text-left p-2 font-semibold">Check-out</th>
                    <th className="text-right p-2 font-semibold">Lordo</th>
                    <th className="text-right p-2 font-semibold">Commiss.</th>
                    <th className="text-right p-2 font-semibold">Fee</th>
                    {preview.source === "airbnb" && <th className="text-right p-2 font-semibold">Tassa</th>}
                    {preview.source === "booking_cedolare" && <th className="text-right p-2 font-semibold">Ritenuta</th>}
                    <th className="text-right p-2 font-semibold">Netto</th>
                    <th className="text-center p-2 font-semibold">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={`${r.reservation_code}-${i}`} className={`border-t border-[#F1ECE4] ${r.status === "CANCELLED" ? "opacity-50 line-through" : ""}`}>
                      <td className="p-2 font-mono text-[10px]">{r.reservation_code}</td>
                      <td className="p-2">{r.guest_name}</td>
                      <td className="p-2 font-mono">{r.check_in}</td>
                      <td className="p-2 font-mono">{r.check_out}</td>
                      <td className="p-2 text-right font-mono">{CURRENCY(r.gross)}</td>
                      <td className="p-2 text-right font-mono">{r.commission ? CURRENCY(r.commission) : "—"}</td>
                      <td className="p-2 text-right font-mono">{r.payment_fee ? CURRENCY(r.payment_fee) : "—"}</td>
                      {preview.source === "airbnb" && <td className="p-2 text-right font-mono">{r.city_tax ? CURRENCY(r.city_tax) : "—"}</td>}
                      {preview.source === "booking_cedolare" && <td className="p-2 text-right font-mono">{r.withholding ? CURRENCY(r.withholding) : "—"}</td>}
                      <td className="p-2 text-right font-mono font-semibold">{CURRENCY(r.net)}</td>
                      <td className="p-2 text-center">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${r.status === "CANCELLED" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{r.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={onConfirm} disabled={confirming} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="btn-confirm-import">
              <Save className="w-4 h-4 mr-2" /> {confirming ? "Importo..." : `Conferma import (${preview.totals?.count || 0} prenotazioni)`}
            </Button>
            <Button onClick={() => { setPreview(null); setFile(null); }} variant="outline" data-testid="btn-cancel-import">
              <X className="w-4 h-4 mr-2" /> Annulla
            </Button>
          </div>
        </div>
      )}

      {/* History */}
      <div>
        <h4 className="font-semibold text-[#1E3A5F] mb-2 text-sm">Storico import ({list.length})</h4>
        <div className="space-y-1.5">
          {list.length === 0 && <div className="text-sm text-[#635F59]">Nessun import ancora</div>}
          {list.map((it) => (
            <div key={it.id} className="border border-[#E8E2D9] rounded-lg p-2.5 text-xs flex items-center flex-wrap gap-3" data-testid={`imp-${it.id}`}>
              <span className="text-[10px] uppercase font-bold tracking-wider text-[#1E3A5F] bg-[#FDFBF7] px-1.5 py-0.5 rounded">{SRC_LABEL[it.source] || it.source}</span>
              <span className="font-mono text-[#635F59]">{it.period_month}</span>
              <span className="text-[#635F59]">{it.reservations_count || "—"} prenot.</span>
              <span className="flex-1 font-mono">
                Lordo <b className="text-emerald-700">{CURRENCY(it.gross)}</b> · Commiss. <b className="text-rose-700">{CURRENCY(it.commission)}</b>{it.payment_fee ? ` · Fee ${CURRENCY(it.payment_fee)}` : ""}{it.withholding ? ` · Ritenuta ${CURRENCY(it.withholding)}` : ""} · Netto <b className="text-[#1E3A5F]">{CURRENCY(it.net)}</b>
              </span>
              {it.document_url && <a href={it.document_url} target="_blank" rel="noreferrer" className="text-[#AC5D45] hover:underline"><FileText className="w-3.5 h-3.5 inline mr-0.5" />File</a>}
              <button onClick={() => onRollback(it.id)} className="text-rose-600 hover:text-rose-800" data-testid={`btn-rollback-${it.id}`} title="Annulla import"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Annual Cedolare / Commissions report */}
      {report && (
        <div className="mt-6 bg-gradient-to-br from-[#FDFBF7] to-white border border-[#E8E2D9] rounded-xl p-4" data-testid="cedolare-annual-report">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h4 className="font-display text-lg text-[#1E3A5F] flex items-center gap-2">
              <Calendar className="w-5 h-5" /> Report annuale cedolare & commissioni
            </h4>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-[#635F59]">Anno</Label>
              <Input
                type="number"
                value={reportYear}
                onChange={(e) => setReportYear(parseInt(e.target.value) || new Date().getFullYear())}
                onBlur={() => loadReport(reportYear)}
                className="w-24 h-8 text-sm font-mono"
                data-testid="cedolare-report-year"
              />
              <Button onClick={() => loadReport(reportYear)} variant="outline" size="sm">
                <RefreshCcw className="w-3.5 h-3.5" />
              </Button>
              <Button
                onClick={async () => {
                  try {
                    const res = await api.get(`/admin/finance/cedolare-annual-report/pdf?year=${reportYear}`, { responseType: "blob" });
                    const blob = new Blob([res.data], { type: "application/pdf" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `Report_Cedolare_${reportYear}_B&B_Emmanuel.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success(`PDF ${reportYear} scaricato`);
                  } catch (e) {
                    toast.error("Errore generazione PDF");
                  }
                }}
                size="sm"
                className="bg-[#AC5D45] hover:bg-[#94472f] text-white"
                data-testid="cedolare-export-pdf"
                title="Genera un riepilogo PDF stampabile per il commercialista"
              >
                <FileText className="w-3.5 h-3.5 mr-1" />
                Esporta PDF
              </Button>
            </div>
          </div>

          {/* Totals row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
              <div className="text-[10px] uppercase text-violet-700 font-semibold">Cedolare secca trattenuta</div>
              <div className="text-xl font-bold text-violet-900 mt-1">{CURRENCY(report.totals.withholding)}</div>
              <div className="text-[10px] text-violet-700 mt-0.5">già versata da Booking</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
              <div className="text-[10px] uppercase text-rose-700 font-semibold">Commissioni Booking</div>
              <div className="text-xl font-bold text-rose-900 mt-1">{CURRENCY(report.totals.commission_booking)}</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-[10px] uppercase text-amber-700 font-semibold">Commissioni Airbnb</div>
              <div className="text-xl font-bold text-amber-900 mt-1">{CURRENCY(report.totals.commission_airbnb)}</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="text-[10px] uppercase text-blue-700 font-semibold">IVA su commissioni</div>
              <div className="text-xl font-bold text-blue-900 mt-1">{CURRENCY(report.totals.vat_commissions)}</div>
              <div className="text-[10px] text-blue-700 mt-0.5">deducibile</div>
            </div>
          </div>

          {/* Monthly breakdown table */}
          <div className="border border-[#E8E2D9] rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#F5F1E8] text-[#1E3A5F]">
                <tr>
                  <th className="px-2 py-2 text-left">Mese</th>
                  <th className="px-2 py-2 text-right">Cedolare secca</th>
                  <th className="px-2 py-2 text-right">Comm. Booking</th>
                  <th className="px-2 py-2 text-right">Comm. Airbnb</th>
                  <th className="px-2 py-2 text-right">IVA commissioni</th>
                </tr>
              </thead>
              <tbody>
                {report.items.map((m) => {
                  const hasData = m.withholding || m.commission_booking || m.commission_airbnb || m.vat_commissions;
                  return (
                    <tr key={m.month} className={`border-t border-[#E8E2D9] ${!hasData ? "opacity-40" : ""}`}>
                      <td className="px-2 py-1.5 font-mono">{m.month}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{m.withholding ? CURRENCY(m.withholding) : "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{m.commission_booking ? CURRENCY(m.commission_booking) : "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{m.commission_airbnb ? CURRENCY(m.commission_airbnb) : "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{m.vat_commissions ? CURRENCY(m.vat_commissions) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-[#635F59] mt-2 leading-relaxed">
            <strong>Nota:</strong> la cedolare secca (21%) è trattenuta e versata direttamente da Booking.com all'Erario per tuo conto. Questo valore ti serve solo per la dichiarazione annuale (modello 730/Unico). L'IVA 22% sulle commissioni OTA è deducibile se sei in regime IVA ordinario (non con cedolare secca pura).
          </p>
        </div>
      )}
    </div>
  );
}
